import { purpurColorGreen600, purpurColorOrange600, purpurColorRed600, } from '@purpurds/tokens/color/variables';
import { CORRECTED, UNCORRECTABLE, UPSTREAM, POWER, MIN_POWER, MAX_POWER, SNR, MIN_SNR, MAX_SNR, DOWNSTREAM, } from 'modules/HealthCheck/constants';
const STATUS_GOOD = purpurColorGreen600;
const STATUS_WARNING = purpurColorOrange600;
const STATUS_ERROR = purpurColorRed600;
/*
  percentHelper is a helper-function to the colorPicker function.
  It is based on calculations reverse engineered from the "cwPercent"
  function, found in healthCheck.js localized by inspecting the SAM - customer view
  page. (Developer tools > Sources > OneUi > js)

  Args:
    property: String, preferably from the ../constants/samSelector.js file
    pollData: Array of objects originating from the SAM-api.

  Returns:
    The percent of corrected or uncorrected values extracted from the array of objects.
*/
const percentHelper = (property, pollData) => {
    const totalSum = pollData.reduce((sum, ch) => sum + ch.Good + ch.Corrected + ch.Uncorrectable, 0);
    if (totalSum === 0)
        return 0;
    if (property === CORRECTED) {
        return pollData.reduce((sum, ch) => sum + ch.Corrected * 100, 0) / totalSum;
    }
    return pollData.reduce((sum, ch) => sum + ch.Uncorrectable * 100, 0) / totalSum;
};
/*
  minMaxHelper is a helper-function to the colorPicker function.
  It returns the maxium, minimum or first element of the data array argument, based
  on the selector argument.

  Args:
    selector: string, preferably from the ../constants/samSelector.js file
    data: Array of objects originating from the SAM-api.

  Returns:
    returnVal: Max or min of data if arg selector includes "MAX" or "MIN", otherwise the
              first element of the array.
*/
const minMaxHelper = (selector, data) => {
    if (!data.length)
        return 0;
    if (selector.includes('MAX')) {
        return Math.max(...data);
    }
    if (selector.includes('MIN')) {
        return Math.min(...data);
    }
    return data[0];
};
/*
  The colorPicker function returns one of three strings based on the attribute:
  selected, channel and the limits.

  The calculations are reverse-engineered from the SAM - customer view page by inspecting
  the healthCheck.js file. (Developer tools > Sources > OneUi > js) functions: cwPercent,
  renderPolledData might be of interest.

  Args:
    scoreVariables: Object, the scoreVariables from the SAM-api. Contains limits.
    selector:       String, the attribute to be tested, i.e. "power"
    channel:        String, "us" for upstream-, "ds" for downstream channel.
    pollData:       Object, the channel(s) from the SAM-api

  Returns:
    statusColor: String. One of three colors based on the limits defined in the SAM-api and the value of the selected attribute.
*/
export const colorPicker = (scoreVariables, selector, channel, pollData) => {
    const { UsPwrLimit1Ch, UsPwrLimit2Ch, UsPwrLimitXCh, DsPwrLimitHigh, DsPwrLimitLow, UsSnrLimit2, UsSnrLimit1, DsSnrLimit, CwCorrLim2, CwCorrLim1, UsUncorrSeverity2, UsUncorrSeverity0, DsUncorrSeverity2, DsUncorrSeverity0, } = scoreVariables;
    const data = Array.isArray(pollData)
        ? pollData
        : [pollData];
    const noOfUpstream = data.length;
    const power = minMaxHelper(selector, data.map(ch => (ch.Power === -1000 ? 0 : ch.Power)));
    const snr = minMaxHelper(selector, data.map(ch => (ch.Snr === -1000 ? 0 : ch.Snr)));
    const corrected = percentHelper(CORRECTED, data);
    const uncorrectable = percentHelper(UNCORRECTABLE, data);
    let statusColor = STATUS_GOOD;
    if (channel === UPSTREAM) {
        switch (selector) {
            case POWER:
            case MIN_POWER:
            case MAX_POWER:
                if ((noOfUpstream === 1 && power >= UsPwrLimit1Ch) ||
                    (noOfUpstream === 2 && power >= UsPwrLimit2Ch) ||
                    (noOfUpstream > 2 && power >= UsPwrLimitXCh)) {
                    statusColor = STATUS_WARNING;
                }
                break;
            case SNR:
            case MIN_SNR:
            case MAX_SNR:
                if (snr <= UsSnrLimit2) {
                    statusColor = STATUS_ERROR;
                }
                else if (snr <= UsSnrLimit1) {
                    statusColor = STATUS_WARNING;
                }
                break;
            case CORRECTED:
                if (corrected >= CwCorrLim2) {
                    statusColor = STATUS_ERROR;
                }
                else if (corrected >= CwCorrLim1) {
                    statusColor = STATUS_WARNING;
                }
                break;
            case UNCORRECTABLE:
                // NOTE: Skipping UsUncorrServerity1. SAM has > 1 in orange and > 0 in yellow.
                if (uncorrectable >= UsUncorrSeverity2) {
                    statusColor = STATUS_ERROR;
                }
                else if (uncorrectable >= UsUncorrSeverity0) {
                    statusColor = STATUS_WARNING;
                }
                break;
            default:
                break;
        }
    }
    else if (channel === DOWNSTREAM) {
        switch (selector) {
            case POWER:
            case MIN_POWER:
            case MAX_POWER:
                if (power >= DsPwrLimitHigh || power <= DsPwrLimitLow) {
                    statusColor = STATUS_WARNING;
                }
                break;
            case SNR:
            case MIN_SNR:
            case MAX_SNR:
                if (snr <= DsSnrLimit) {
                    statusColor = STATUS_WARNING;
                }
                break;
            case CORRECTED:
                if (corrected >= CwCorrLim2) {
                    statusColor = STATUS_ERROR;
                }
                else if (corrected >= CwCorrLim1) {
                    statusColor = STATUS_WARNING;
                }
                break;
            case UNCORRECTABLE:
                // NOTE: Skipping UsUncorrServerity1. SAM has > 1 in orange and > 0 in yellow.
                if (uncorrectable >= DsUncorrSeverity2) {
                    statusColor = STATUS_ERROR;
                }
                else if (uncorrectable >= DsUncorrSeverity0) {
                    statusColor = STATUS_WARNING;
                }
                break;
            default:
                break;
        }
    }
    return statusColor;
};
